import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BadgeCheck,
  BarChart3,
  CalendarClock,
  ChevronRight,
  CircleDollarSign,
  CreditCard,
  Crown,
  Download,
  ExternalLink,
  FileText,
  Lock,
  Receipt,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppBackground from "../../components/ui/AppBackground";
import Button from "../../components/ui/Button";
import { useToast } from "../../hooks/use-toast";

// Optional: If you already use PayPal subscriptions inside the app area
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";
const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID;

function getAccessToken() {
  return localStorage.getItem("access_token") || sessionStorage.getItem("access_token") || "";
}

async function fetchJSON(path, token, opts = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : { raw: await res.text() };
  if (!res.ok) {
    const msg = data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function money(amount, currency = "USD") {
  const n = Number(amount ?? 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusPill(status) {
  const s = String(status || "").toLowerCase();
  const map = {
    active: "bg-emerald-500/15 text-emerald-200 border-emerald-500/20",
    completed: "bg-emerald-500/15 text-emerald-200 border-emerald-500/20",
    paid: "bg-emerald-500/15 text-emerald-200 border-emerald-500/20",
    pending: "bg-amber-500/15 text-amber-200 border-amber-500/20",
    open: "bg-amber-500/15 text-amber-200 border-amber-500/20",
    canceled: "bg-rose-500/15 text-rose-200 border-rose-500/20",
    cancelled: "bg-rose-500/15 text-rose-200 border-rose-500/20",
    failed: "bg-rose-500/15 text-rose-200 border-rose-500/20",
    draft: "bg-white/6 text-white/70 border-white/10",
  };
  const cls = map[s] || "bg-white/6 text-white/70 border-white/10";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${cls}`}>
      {status || "—"}
    </span>
  );
}

function GlassCard({ children, className = "" }) {
  return (
    <div
      className={`relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_20px_70px_-35px_rgba(99,102,241,0.55)] ${className}`}
    >
      <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(circle_at_20%_10%,rgba(99,102,241,0.18),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(168,85,247,0.14),transparent_55%)]" />
      <div className="relative">{children}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="flex items-center gap-2 text-white/70">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="mt-2 text-lg font-semibold text-white truncate">{value}</div>
      {hint ? <div className="mt-1 text-xs text-white/45">{hint}</div> : null}
    </div>
  );
}

function NavItem({ to, icon: Icon, title, desc, active }) {
  return (
    <Link
      to={to}
      className={`group flex items-start gap-3 rounded-2xl px-3 py-3 border transition-all
        ${active ? "border-white/20 bg-white/[0.06]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05] hover:border-white/15"}`}
    >
      <div className={`mt-0.5 h-9 w-9 rounded-xl flex items-center justify-center border ${active ? "border-indigo-400/30 bg-indigo-500/10" : "border-white/10 bg-white/5"}`}>
        <Icon className={`h-5 w-5 ${active ? "text-indigo-200" : "text-white/70 group-hover:text-white"}`} />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className={`text-sm font-semibold ${active ? "text-white" : "text-white/85"}`}>{title}</div>
          <ChevronRight className={`h-4 w-4 transition-all ${active ? "opacity-100 text-white/60" : "opacity-0 -translate-x-1 text-white/40 group-hover:opacity-100 group-hover:translate-x-0"}`} />
        </div>
        <div className="text-xs text-white/50 mt-0.5 line-clamp-2">{desc}</div>
      </div>
    </Link>
  );
}

function RadialUsage({ pct = 0, title = "Usage" }) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  const bg = `conic-gradient(rgba(99,102,241,.95) ${clamped}%, rgba(255,255,255,.10) 0)`;
  return (
    <div className="flex items-center gap-5">
      <div className="relative h-28 w-28 rounded-full" style={{ background: bg }}>
        <div className="absolute inset-[10px] rounded-full bg-[#07090c] border border-white/10" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-xl font-bold text-white">{clamped.toFixed(0)}%</div>
            <div className="text-[11px] text-white/50">{title}</div>
          </div>
        </div>
        <div className="pointer-events-none absolute -inset-6 rounded-full bg-indigo-500/10 blur-2xl" />
      </div>
      <div className="flex-1">
        <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${clamped}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-white/55">
          <span>{pct??0}%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Main Page
// ------------------------------------------------------------
export default function SubscriptionCenter() {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => getAccessToken(), []);

  const activeKey = useMemo(() => {
    const p = location.pathname || "";
    if (p.startsWith("/my-subscription/usage")) return "usage";
    if (p.startsWith("/my-subscription")) return "overview";
    if (p.startsWith("/my-invoices")) return "invoices";
    if (p.startsWith("/my-payments")) return "payments";
    if (p.startsWith("/plans")) return "upgrade";
    return "overview";
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);

  const [panelLoading, setPanelLoading] = useState({ usage: false, invoices: false, payments: false, upgrade: false });

  const [invoiceDrawer, setInvoiceDrawer] = useState({ open: false, invoice: null, relatedPayment: null });

  // Initial load: subscription + usage (for header stats)
  useEffect(() => {
    let mounted = true;

    async function run() {
      if (!token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [subRes, usageRes] = await Promise.allSettled([
          fetchJSON("/api/subscriptions/my-subscription", token),
          fetchJSON("/api/subscriptions/my-subscription/usage", token),
        ]);

        if (!mounted) return;

        if (subRes.status === "fulfilled" && subRes.value?.success !== false) {
          setSub(subRes.value.subscription || subRes.value);
        } else {
          // No active subscription -> keep null (UX will show free/renew state)
          setSub(null);
        }

        if (usageRes.status === "fulfilled") {
          setUsage(usageRes.value.usage || null);
        } else {
          setUsage(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    run();
    return () => {
      mounted = false;
    };
  }, [token]);

  // Lazy-load panels
  useEffect(() => {
    if (!token) return;

    const loadInvoices = async () => {
      setPanelLoading((p) => ({ ...p, invoices: true }));
      try {
        const res = await fetchJSON("/api/subscriptions/my-invoices", token);
        setInvoices(res.invoices || []);
      } catch (e) {
        // If backend doesn't have these endpoints yet
        setInvoices([]);
      } finally {
        setPanelLoading((p) => ({ ...p, invoices: false }));
      }
    };

    const loadPayments = async () => {
      setPanelLoading((p) => ({ ...p, payments: true }));
      try {
        const res = await fetchJSON("/api/subscriptions/my-payments", token);
        setPayments(res.payments || []);
      } catch (e) {
        setPayments([]);
      } finally {
        setPanelLoading((p) => ({ ...p, payments: false }));
      }
    };

    const loadPlans = async () => {
      setPanelLoading((p) => ({ ...p, upgrade: true }));
      try {
        const res = await fetchJSON("/api/subscriptions/plans", token);
        setPlans(res.plans || []);
      } catch (e) {
        setPlans([]);
      } finally {
        setPanelLoading((p) => ({ ...p, upgrade: false }));
      }
    };

    if (activeKey === "invoices") {
      if (invoices.length === 0) loadInvoices();
      // invoices often need receipt_url from payments for a perfect "Download" UX
      if (payments.length === 0) loadPayments();
    }
    if (activeKey === "payments" && payments.length === 0) loadPayments();
    if (activeKey === "upgrade" && plans.length === 0) loadPlans();
  }, [activeKey, token]);

  const currentPlanName = sub?.plan?.name || "Free";
  const currentPlanType = sub?.plan?.plan_type || "free";
  const status = sub?.status || (sub ? "active" : "free");
  const end = sub?.current_period_end;

  const usagePct = useMemo(() => {
    if (!usage) return 0;
    const pct = Number(usage.usage_percentage ?? 0);
    if (Number.isFinite(pct)) return pct;
    const used = Number(usage.interviews_used ?? 0);
    const limit = Number(usage.interviews_limit ?? 0);
    return limit > 0 ? (used / limit) * 100 : 0;
  }, [usage]);

  const interviewsUsed = usage?.interviews_used ?? sub?.interviews_used ?? 0;
  const interviewsLimit = usage?.interviews_limit ?? sub?.interviews_limit ?? 0;
  const interviewsRemaining = usage?.interviews_remaining ?? Math.max(0, (interviewsLimit || 0) - (interviewsUsed || 0));

  // Drawer helpers
  const openInvoice = async (invoice) => {
    const related = payments.find((p) => p.id === invoice.payment_id) || null;
    setInvoiceDrawer({ open: true, invoice, relatedPayment: related });

    // If you want freshest invoice detail, uncomment this:
    // try {
    //   const res = await fetchJSON(`/api/subscriptions/invoice/${invoice.invoice_number}`, token);
    //   setInvoiceDrawer({ open: true, invoice: res.invoice, relatedPayment: related });
    // } catch {}
  };

  const downloadInvoice = (invoice) => {
    if (invoice?.pdf_url) {
      window.open(invoice.pdf_url, "_blank", "noopener,noreferrer");
      return;
    }
    const related = payments.find((p) => p.id === invoice.payment_id);
    if (related?.receipt_url) {
      window.open(related.receipt_url, "_blank", "noopener,noreferrer");
      return;
    }
    toast({
      variant: "destructive",
      title: "Receipt not available",
      description: "This invoice does not have a downloadable PDF or receipt URL yet.",
    });
  };

  const downloadReceiptFromPayment = (payment) => {
    if (payment?.receipt_url) {
      window.open(payment.receipt_url, "_blank", "noopener,noreferrer");
      return;
    }
    toast({
      variant: "destructive",
      title: "Receipt not available",
      description: "This payment does not have a receipt URL yet.",
    });
  };

  const cancelSubscription = async () => {
    if (!sub?.paypal_subscription_id) {
      toast({ variant: "destructive", title: "No PayPal subscription", description: "We can't find a PayPal subscription id on your account." });
      return;
    }

    const ok = window.confirm(
      "Cancel your subscription now?\n\nYou may lose premium access immediately depending on your backend cancellation logic."
    );
    if (!ok) return;

    try {
      await fetchJSON("/api/subscriptions/paypal/cancel", token, { method: "POST" });
      toast({ title: "Subscription cancelled", description: "Your subscription was cancelled." });
      // reload
      const subRes = await fetchJSON("/api/subscriptions/my-subscription", token);
      setSub(subRes.subscription || subRes);
    } catch (e) {
      toast({ variant: "destructive", title: "Cancel failed", description: e.message || "Please try again." });
    }
  };

  if (!token) {
    return (
      <AppBackground className="min-h-screen bg-[#07090c] text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_20%_10%,rgba(99,102,241,0.35),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(168,85,247,0.30),transparent_45%),radial-gradient(circle_at_50%_100%,rgba(34,211,238,0.18),transparent_50%)]" />
          <div className="absolute inset-0 opacity-[0.18] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:64px_64px]" />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 pt-28 pb-20">
          <GlassCard className="p-8">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Lock className="h-6 w-6 text-indigo-200" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold">Billing & Subscription</h1>
                <p className="mt-2 text-white/60">
                  Please log in to view your plan, usage, invoices and payments.
                </p>
                <div className="mt-6 flex gap-3">
                  <Link to="/login">
                    <Button className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500">
                      Go to Login
                    </Button>
                  </Link>
                  <Link to="/plans">
                    <Button variant="outline" className="border-white/10 hover:bg-white/5">
                      View Plans
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </AppBackground>
    );
  }

  return (
    <AppBackground className="min-h-screen  text-white overflow-hidden">
      {/* Futuristic overlays */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_20%_10%,rgba(99,102,241,0.35),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(168,85,247,0.30),transparent_45%),radial-gradient(circle_at_50%_100%,rgba(34,211,238,0.18),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.18] grid-sweep [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:64px_64px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
        <div className="absolute -top-28 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-indigo-500/10 blur-[90px]" />
        <div className="absolute -bottom-40 right-[-120px] h-[520px] w-[520px] rounded-full bg-purple-500/10 blur-[100px]" />
      </div>

      {/* Top bar */}
      <nav className=" w-full py-4 px-4 sm:px-6  backdrop-blur-2xl" style={{ zIndex: 99999 }}>
        <div className="max-w-full mx-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            
            <div className="flex flex-col leading-tight">
              <h1 className="text-sm sm:text-base font-semibold text-foreground">Billing Center</h1>
              {/* <span className="text-[11px] text-muted-foreground">Billing Center</span> */}
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
              <ShieldCheck className="h-4 w-4 text-emerald-300" />
              <span className="text-xs text-white/75">Secure payments via PayPal</span>
            </div>
            <Link to="/dashboard-home" className="text-sm text-white/70 hover:text-white transition-colors">
              Dashboard
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative z-10 pt-28 pb-16">
        <div className="max-w-full mx-2 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl">
                <Sparkles className="h-4 w-4 text-purple-300" />
                <span className="text-xs text-white/80">Subscription & billing</span>
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold">
                <span className="bg-gradient-to-r from-white via-white to-white/70 bg-clip-text text-transparent">Your Plan:</span>{" "}
                <span className="bg-gradient-to-r from-purple-300 via-indigo-300 to-cyan-200 bg-clip-text text-transparent">
                  {currentPlanName}
                </span>
              </h2>
              <p className="mt-2 text-white/60">
                Track usage, download receipts, and upgrade anytime — built for high trust and clarity.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full lg:w-auto">
              <Stat icon={Crown} label="Status" value={statusPill(status)} />
              <Stat icon={BarChart3} label="Interviews" value={`${interviewsRemaining} left`} hint={`${interviewsUsed} used`} />
              <Stat icon={CalendarClock} label="Renews" value={end ? fmtDate(end) : "—"} hint={sub?.billing_cycle ? String(sub.billing_cycle).toUpperCase() : "—"} />
              <Stat icon={CircleDollarSign} label="Price" value={sub ? money(sub.final_price ?? sub.price, "USD") : "Free"} hint={sub?.has_student_discount ? "Discount applied" : ""} />
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sidebar */}
            <aside className="lg:col-span-4 xl:col-span-3 space-y-3">
              <NavItem
                to="/my-subscription"
                icon={BadgeCheck}
                title="Overview"
                desc="Current plan, renewal date, key actions."
                active={activeKey === "overview"}
              />
              <NavItem
                to="/my-subscription/usage"
                icon={BarChart3}
                title="Usage"
                desc="Monthly usage, limits, reset timeline."
                active={activeKey === "usage"}
              />
              <NavItem
                to="/my-invoices"
                icon={FileText}
                title="Invoices"
                desc="Invoice history, PDFs, billing totals."
                active={activeKey === "invoices"}
              />
              <NavItem
                to="/my-payments"
                icon={CreditCard}
                title="Payments"
                desc="Transaction status, receipts, references."
                active={activeKey === "payments"}
              />
              <NavItem
                to="/plans"
                icon={Rocket}
                title="Upgrade"
                desc="Change plan with PayPal subscriptions."
                active={activeKey === "upgrade"}
              />

              <div className="pt-3">
                <GlassCard className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-emerald-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white">Trust layer</div>
                      <div className="mt-1 text-xs text-white/55">
                        We never store card details. All billing is handled by PayPal.
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </aside>

            {/* Content */}
            <section className="lg:col-span-8 xl:col-span-9">
              {loading ? (
                <GlassCard className="p-8">
                  <div className="flex items-center gap-3 text-white/70">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    <span>Loading your billing center…</span>
                  </div>
                </GlassCard>
              ) : (
                <>
                  {activeKey === "overview" && (
                    <OverviewPanel
                      sub={sub}
                      usage={usage}
                      token={token}
                      onUpgrade={() => navigate("/plans")}
                      onCancel={cancelSubscription}
                    />
                  )}

                  {activeKey === "usage" && (
                    <UsagePanel usage={usage} sub={sub} onUpgrade={() => navigate("/plans")} />
                  )}

                  {activeKey === "invoices" && (
                    <InvoicesPanel
                      invoices={invoices}
                      payments={payments}
                      loading={panelLoading.invoices}
                      onOpen={openInvoice}
                      onDownload={downloadInvoice}
                      onGoUpgrade={() => navigate("/plans")}
                    />
                  )}

                  {activeKey === "payments" && (
                    <PaymentsPanel
                      payments={payments}
                      loading={panelLoading.payments}
                      onDownload={downloadReceiptFromPayment}
                      onGoUpgrade={() => navigate("/plans")}
                    />
                  )}

                  {activeKey === "upgrade" && (
                    <UpgradePanel
                      plans={plans}
                      loading={panelLoading.upgrade}
                      token={token}
                      currentPlanType={currentPlanType}
                      onSuccess={() => {
                        toast({ title: "Plan updated", description: "Your subscription is active." });
                        navigate("/my-subscription");
                        // refresh subscription
                        fetchJSON("/api/subscriptions/my-subscription", token)
                          .then((r) => setSub(r?.success === false ? null : (r.subscription || r)))
                          .catch(() => {});
                        fetchJSON("/api/subscriptions/my-subscription/usage", token)
                          .then((r) => setUsage(r.usage || null))
                          .catch(() => {});
                      }}
                    />
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* Invoice drawer */}
      {invoiceDrawer.open && (
        <InvoiceDrawer
          invoice={invoiceDrawer.invoice}
          payment={invoiceDrawer.relatedPayment}
          onClose={() => setInvoiceDrawer({ open: false, invoice: null, relatedPayment: null })}
          onDownload={() => downloadInvoice(invoiceDrawer.invoice)}
        />
      )}

      <style>{`
        .grid-sweep { animation: gridSweep 18s linear infinite; will-change: transform; }
        @keyframes gridSweep { 0% { transform: translate3d(0,0,0); } 100% { transform: translate3d(-64px,-64px,0); } }
      `}</style>
    </AppBackground>
  );
}

// ------------------------------------------------------------
// Panels
// ------------------------------------------------------------
function OverviewPanel({ sub, usage, onUpgrade, onCancel }) {
  const hasSub = Boolean(sub);

  if (!hasSub) {
    return (
      <GlassCard className="p-8">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Crown className="h-6 w-6 text-indigo-200" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white">You’re on the Free plan</h3>
            <p className="mt-2 text-white/60">
              Upgrade to unlock higher interview limits, advanced analytics, and premium AI evaluation.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onUpgrade}
                className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
              >
                View Plans
              </Button>
              <Link to="/pricing" className="sm:self-center text-sm text-white/65 hover:text-white inline-flex items-center gap-2">
                Explore pricing page <ExternalLink className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-6">
      <GlassCard className="p-8">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
              <BadgeCheck className="h-4 w-4 text-emerald-300" />
              <span className="text-xs text-white/80">Current subscription</span>
            </div>
            <h3 className="mt-3 text-2xl font-bold text-white">
              {sub?.plan?.name || "Premium"}
              <span className="ml-2 text-sm font-semibold text-white/55">
                · {String(sub?.billing_cycle || "monthly").toUpperCase()}
              </span>
            </h3>
            <p className="mt-2 text-white/60 max-w-2xl">
              {sub?.plan?.description || "Your subscription gives you premium access and a higher monthly quota."}
            </p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-xs text-white/55">Period</div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {fmtDate(sub?.current_period_start)} → {fmtDate(sub?.current_period_end)}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-xs text-white/55">PayPal subscription</div>
                <div className="mt-1 text-sm font-semibold text-white truncate">
                  {sub?.paypal_subscription_id || "—"}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-xs text-white/55">Billing status</div>
                <div className="mt-2">{statusPill(sub?.status)}</div>
              </div>
            </div>
          </div>

          <div className="xl:w-[420px] w-full">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white">This month usage</div>
                <div className="text-xs text-white/50">Resets automatically</div>
              </div>
              <div className="mt-5">
                <RadialUsage pct={usage?.usage_percentage ?? 0} title="used" />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] py-3">
                  <div className="text-xs text-white/55">Used</div>
                  <div className="text-lg font-bold text-white">{usage?.interviews_used ?? sub?.interviews_used ?? 0}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] py-3">
                  <div className="text-xs text-white/55">Limit</div>
                  <div className="text-lg font-bold text-white">{usage?.interviews_limit ?? sub?.interviews_limit ?? 0}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] py-3">
                  <div className="text-xs text-white/55">Left</div>
                  <div className="text-lg font-bold text-white">{usage?.interviews_remaining ?? 0}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onUpgrade}
            className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
          >
            Upgrade / Change Plan
          </Button>
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-white/10 hover:bg-white/5"
          >
            Cancel Subscription
          </Button>
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-indigo-200" />
              <div className="text-sm font-semibold">Receipts & invoices</div>
            </div>
            <Link to="/my-invoices" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1">
              Open <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-2 text-sm text-white/60">
            Download invoices and receipts for accounting, reimbursement, or compliance.
          </p>
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white/75" />
              </div>
              <div className="text-sm text-white/65">
                Tip: invoices appear after confirmation and may be finalized by webhook within seconds.
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-300" />
              <div className="text-sm font-semibold">Premium guarantee</div>
            </div>
          </div>
          <p className="mt-2 text-sm text-white/60">
            If you paid and don’t see premium access, open support — we can reconcile using PayPal IDs.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-xs text-white/55">Support</div>
              <div className="mt-1 text-sm font-semibold text-white">24–48h</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-xs text-white/55">Verification</div>
              <div className="mt-1 text-sm font-semibold text-white">Webhook-backed</div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

function UsagePanel({ usage, sub, onUpgrade }) {
  const has = Boolean(usage);

  if (!has) {
    return (
      <GlassCard className="p-8">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <BarChart3 className="h-6 w-6 text-indigo-200" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold">No usage data</h3>
            <p className="mt-2 text-white/60">Upgrade to unlock usage tracking, analytics, and larger quotas.</p>
            <div className="mt-6">
              <Button
                onClick={onUpgrade}
                className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
              >
                View Plans
              </Button>
            </div>
          </div>
        </div>
      </GlassCard>
    );
  }

  const pct = Number(usage.usage_percentage ?? 0);
  const used = Number(usage.interviews_used ?? 0);
  const limit = Number(usage.interviews_limit ?? 0);
  const remaining = Number(usage.interviews_remaining ?? Math.max(0, limit - used));

  return (
    <div className="space-y-6">
      <GlassCard className="p-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
              <BarChart3 className="h-4 w-4 text-indigo-200" />
              <span className="text-xs text-white/80">Monthly usage</span>
            </div>
            <h3 className="mt-3 text-2xl font-bold text-white">Usage & Limits</h3>
            <p className="mt-2 text-white/60 max-w-xl">
              Stay aware of your monthly quota. We’ll reset usage automatically on your reset date.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 w-full lg:w-auto">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center">
              <div className="text-xs text-white/55">Used</div>
              <div className="mt-1 text-xl font-bold text-white">{used}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center">
              <div className="text-xs text-white/55">Remaining</div>
              <div className="mt-1 text-xl font-bold text-white">{remaining}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center">
              <div className="text-xs text-white/55">Limit</div>
              <div className="mt-1 text-xl font-bold text-white">{limit}</div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <RadialUsage pct={pct} title="used" />
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-xs text-white/55">Reset date</div>
            <div className="mt-1 text-sm font-semibold text-white">{fmtDate(usage.reset_date)}</div>
            <div className="mt-2 text-xs text-white/50">{usage.days_until_reset ?? 0} days remaining</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-xs text-white/55">Eligibility</div>
            <div className="mt-2">{statusPill(usage.can_use_interview ? "active" : "pending")}</div>
            <div className="mt-2 text-xs text-white/50">{usage.can_use_interview ? "You can start a new interview." : "Limit reached for this period."}</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-xs text-white/55">Plan</div>
            <div className="mt-1 text-sm font-semibold text-white">{sub?.plan?.name || "Free"}</div>
            <div className="mt-2">
              <Button onClick={onUpgrade} variant="outline" className="border-white/10 hover:bg-white/5">
                Upgrade
              </Button>
            </div>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}

function InvoicesPanel({ invoices, payments, loading, onOpen, onDownload, onGoUpgrade }) {
  return (
    <GlassCard className="p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
            <FileText className="h-4 w-4 text-indigo-200" />
            <span className="text-xs text-white/80">Invoices</span>
          </div>
          <h3 className="mt-3 text-2xl font-bold text-white">Billing documents</h3>
          <p className="mt-2 text-white/60">Download invoices for reimbursements, accounting, or records.</p>
        </div>
        <Button onClick={onGoUpgrade} className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500">
          Upgrade
        </Button>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center gap-3 text-white/70">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading invoices…</span>
          </div>
        ) : invoices?.length ? (
          <div className="divide-y divide-white/10 rounded-2xl border border-white/10 overflow-hidden">
            {invoices.map((inv) => (
              <div key={inv.id} className="bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-semibold text-white truncate">{inv.invoice_number}</div>
                      {statusPill(inv.status)}
                    </div>
                    <div className="mt-1 text-xs text-white/55">
                      Invoice date: {fmtDate(inv.invoice_date)} · Total: {money(inv.total_amount, "USD")}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="border-white/10 hover:bg-white/5"
                      onClick={() => onOpen(inv)}
                    >
                      Details
                    </Button>
                    <Button
                      className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
                      onClick={() => onDownload(inv)}
                    >
                      <Download className="h-4 w-4 mr-2" /> Download
                    </Button>
                  </div>
                </div>

                {/* micro receipt hint */}
                <div className="mt-3 text-xs text-white/45">
                  {inv.pdf_url ? "PDF available" : (payments.find((p) => p.id === inv.payment_id)?.receipt_url ? "Receipt available" : "Receipt pending (webhook may finalize shortly)")}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-white/70">
            No invoices yet.
          </div>
        )}
      </div>
    </GlassCard>
  );
}

function PaymentsPanel({ payments, loading, onDownload, onGoUpgrade }) {
  return (
    <GlassCard className="p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
            <CreditCard className="h-4 w-4 text-indigo-200" />
            <span className="text-xs text-white/80">Payments</span>
          </div>
          <h3 className="mt-3 text-2xl font-bold text-white">Transactions</h3>
          <p className="mt-2 text-white/60">See payment status, invoice references and download receipts.</p>
        </div>
        <Button onClick={onGoUpgrade} className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500">
          Upgrade
        </Button>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center gap-3 text-white/70">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading payments…</span>
          </div>
        ) : payments?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {payments.map((p) => (
              <div key={p.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-white">{money(p.amount, p.currency || "USD")}</div>
                      {statusPill(p.status)}
                    </div>
                    <div className="mt-1 text-xs text-white/55">
                      {p.invoice_number ? `Invoice: ${p.invoice_number}` : "No invoice number"}
                    </div>
                    <div className="mt-1 text-xs text-white/45">
                      Paid: {p.paid_at ? fmtDate(p.paid_at) : "—"} · Created: {fmtDate(p.created_at)}
                    </div>
                  </div>

                  <div className="h-10 w-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Receipt className="h-5 w-5 text-white/70" />
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <Button
                    onClick={() => onDownload(p)}
                    className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
                  >
                    <Download className="h-4 w-4 mr-2" /> Receipt
                  </Button>
                  {p.receipt_url ? (
                    <a
                      href={p.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2"
                    >
                      Open <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : (
                    <span className="text-xs text-white/45">Receipt pending</span>
                  )}
                </div>

                {p.description ? <div className="mt-3 text-xs text-white/50">{p.description}</div> : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-white/70">
            No payments yet.
          </div>
        )}
      </div>
    </GlassCard>
  );
}

function UpgradePanel({ plans, loading, token, currentPlanType, onSuccess }) {
  const { toast } = useToast();

  const safePlans = (plans || []).filter((p) => p?.is_active !== false).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  const initialPayPalOptions = useMemo(
    () => ({
      clientId: PAYPAL_CLIENT_ID,
      vault: true,
      intent: "subscription",
      components: "buttons",
    }),
    []
  );

  const handleApprove = async (subscriptionID) => {
    try {
      const res = await fetchJSON("/api/subscriptions/paypal/confirm-subscription", token, {
        method: "POST",
        body: JSON.stringify({ subscription_id: subscriptionID }),
      });
      toast({ title: "Subscription verified", description: res.message || "Your plan is active." });
      onSuccess?.();
    } catch (e) {
      toast({ variant: "destructive", title: "Subscription failed", description: e.message || "Please try again." });
    }
  };

  return (
    <GlassCard className="p-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
            <Rocket className="h-4 w-4 text-indigo-200" />
            <span className="text-xs text-white/80">Upgrade</span>
          </div>
          <h3 className="mt-3 text-2xl font-bold text-white">Choose a plan</h3>
          <p className="mt-2 text-white/60 max-w-2xl">
            Upgrade instantly with PayPal subscriptions. Your plan activates after verification.
          </p>
        </div>

        <Link to="/pricing" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2">
          See marketing pricing page <ExternalLink className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex items-center gap-3 text-white/70">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading plans…</span>
          </div>
        ) : !PAYPAL_CLIENT_ID ? (
          <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-6 text-rose-100">
            <div className="font-semibold">Missing PayPal client id</div>
            <div className="mt-1 text-sm text-rose-100/80">
              Set <span className="font-mono">VITE_PAYPAL_CLIENT_ID</span> in your frontend environment.
            </div>
          </div>
        ) : safePlans.length ? (
          <PayPalScriptProvider options={initialPayPalOptions}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {safePlans.map((p) => {
                const isCurrent = String(p.plan_type) === String(currentPlanType);
                const isPopular = Boolean(p.is_popular);
                return (
                  <div
                    key={p.id}
                    className={`relative rounded-3xl border p-6 overflow-hidden transition-all
                      ${isCurrent ? "border-emerald-500/30 bg-emerald-500/5" : isPopular ? "border-indigo-400/30 bg-white/[0.04]" : "border-white/10 bg-white/[0.03]"}`}
                  >
                    <div className="pointer-events-none absolute inset-0 opacity-70 bg-[radial-gradient(circle_at_25%_10%,rgba(99,102,241,0.18),transparent_55%),radial-gradient(circle_at_85%_0%,rgba(168,85,247,0.14),transparent_55%)]" />
                    <div className="relative">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-lg font-bold text-white">{p.name}</div>
                          <div className="mt-1 text-sm text-white/60">{p.description}</div>
                        </div>
                        {isCurrent ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border border-emerald-500/20 bg-emerald-500/15 text-emerald-200">
                            <BadgeCheck className="h-3.5 w-3.5" /> Current
                          </span>
                        ) : isPopular ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border border-indigo-500/20 bg-indigo-500/15 text-indigo-200">
                            <Sparkles className="h-3.5 w-3.5" /> Popular
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-5 flex items-end justify-between">
                        <div className="text-3xl font-black text-white">{money(p.monthly_price, "USD")}</div>
                        <div className="text-xs text-white/50">per month</div>
                      </div>

                      <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                        <div className="text-xs text-white/55">Includes</div>
                        <div className="mt-2 text-sm text-white">
                          {p.interviews_per_month} interviews / month
                        </div>
                        {Array.isArray(p.features) && p.features.length ? (
                          <ul className="mt-3 space-y-2 text-sm text-white/70">
                            {p.features.slice(0, 5).map((f, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-300/80" />
                                <span className="line-clamp-2">{f}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>

                      <div className="mt-5">
                        {p.paypal_plan_id ? (
                          <PayPalButtons
                            style={{ layout: "vertical", label: "subscribe", shape: "pill", height: 46, color: isPopular ? "gold" : "white" }}
                            createSubscription={(data, actions) => actions.subscription.create({ plan_id: p.paypal_plan_id })}
                            onApprove={(data) => handleApprove(data.subscriptionID)}
                            onError={(err) => {
                              console.error("PayPal error", err);
                              toast({ variant: "destructive", title: "PayPal error", description: "Payment processing failed." });
                            }}
                            disabled={isCurrent}
                          />
                        ) : (
                          <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-4 text-sm text-rose-200 flex items-center gap-2">
                            <XCircle className="h-4 w-4" /> Missing PayPal plan ID
                          </div>
                        )}
                      </div>

                      {isCurrent ? (
                        <div className="mt-3 text-xs text-emerald-200/80">You’re already on this plan.</div>
                      ) : (
                        <div className="mt-3 text-xs text-white/45">Secure checkout in a PayPal popup.</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </PayPalScriptProvider>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-white/70">
            No plans available.
          </div>
        )}
      </div>
    </GlassCard>
  );
}

// ------------------------------------------------------------
// Invoice Drawer
// ------------------------------------------------------------
function InvoiceDrawer({ invoice, payment, onClose, onDownload }) {
  if (!invoice) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />

      <div className="relative w-full sm:max-w-2xl mx-auto">
        <div className="m-4 sm:m-6 rounded-3xl border border-white/10 bg-[#0b0c12]/90 backdrop-blur-2xl shadow-[0_35px_120px_-60px_rgba(99,102,241,0.9)]">
          <div className="p-5 sm:p-6 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-indigo-200" />
              </div>
              <div>
                <div className="text-sm text-white/60">Invoice</div>
                <div className="text-lg font-bold text-white">{invoice.invoice_number}</div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center"
              aria-label="Close"
            >
              <XCircle className="h-5 w-5 text-white/60" />
            </button>
          </div>

          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-xs text-white/55">Status</div>
                <div className="mt-2">{statusPill(invoice.status)}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-xs text-white/55">Invoice date</div>
                <div className="mt-1 text-sm font-semibold text-white">{fmtDate(invoice.invoice_date)}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-xs text-white/55">Total</div>
                <div className="mt-1 text-xl font-bold text-white">{money(invoice.total_amount, "USD")}</div>
                <div className="mt-1 text-xs text-white/45">
                  Subtotal {money(invoice.subtotal, "USD")} · Discount {money(invoice.discount_amount || 0, "USD")}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-xs text-white/55">Payment</div>
                <div className="mt-1 text-sm font-semibold text-white">{payment ? money(payment.amount, payment.currency || "USD") : "—"}</div>
                <div className="mt-1 text-xs text-white/45">{payment?.paid_at ? `Paid ${fmtDate(payment.paid_at)}` : "Not paid yet"}</div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onDownload}
                className="button-glow bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500"
              >
                <Download className="h-4 w-4 mr-2" /> Download receipt
              </Button>

              {invoice.pdf_url ? (
                <a
                  href={invoice.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-sm font-semibold text-white/85"
                >
                  Open PDF <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              ) : payment?.receipt_url ? (
                <a
                  href={payment.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-sm font-semibold text-white/85"
                >
                  Open PayPal receipt <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              ) : null}
            </div>

            <div className="mt-5 text-xs text-white/45">
              If your receipt isn’t available yet, it may be finalized by the PayPal webhook shortly.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
